import { Controller } from "@hotwired/stimulus";

// Compact mortgage-center calculator for the Account dashboard (SUR redesign).
// A small "what's my payment" widget: an editable rate, a home price, and a
// down-payment percentage, resolving to an estimated monthly P&I. This is the
// lightweight sibling of connect_affordability_controller — no donut, no taxes,
// just principal & interest so it fits inside an overview card. Server markup
// lives inline in connect/account/show; this controller recomputes on input.
export default class extends Controller {
  static targets = ["rate", "price", "down", "payment"];
  static values = { term: { type: Number, default: 360 } };

  connect() {
    this.recompute();
  }

  recompute() {
    const price = this.num(this.priceTarget, 0);
    const downPct = this.num(this.downTarget, 0) / 100;
    const rate = this.num(this.rateTarget, 0);
    const loan = Math.max(price - price * downPct, 0);
    const pi = this.monthlyPayment(loan, rate, this.termValue);
    if (this.hasPaymentTarget) this.paymentTarget.textContent = this.money(pi);
  }

  // Reformat the price field to a grouped currency string once the user leaves
  // it (keeps the raw digits editable while typing).
  formatPrice() {
    if (!this.hasPriceTarget) return;
    const value = this.num(this.priceTarget, 0);
    this.priceTarget.value = value > 0 ? `$${Math.round(value).toLocaleString("en-US")}` : "";
  }

  // Standard amortized monthly P&I (mirrors connect_affordability_controller).
  monthlyPayment(loan, ratePct, termMonths) {
    if (loan <= 0 || termMonths <= 0) return 0;
    const mRate = ratePct / 100 / 12;
    if (mRate <= 0) return loan / termMonths;
    return (mRate * loan * Math.pow(1 + mRate, termMonths)) / (Math.pow(1 + mRate, termMonths) - 1);
  }

  num(t, fallback = 0) {
    if (!t) return fallback;
    const v = parseFloat(String(t.value).replace(/[^0-9.]/g, ""));
    return Number.isFinite(v) ? v : fallback;
  }

  money(v) {
    if (!Number.isFinite(v)) return "—";
    return `$${Math.round(v).toLocaleString("en-US")}`;
  }
}
